import React from "react";

import { AuthByEmail } from "@pages/auth/AuthByEmail";
import { LoginByPhoneNumber } from "@pages/auth/LoginByPhoneNumber";
import { B2BLogin } from "@pages/auth/B2BLogin";
import { B2BAutoLoginByToken } from "@pages/auth/B2BAutoLoginByToken";
import { VerifyPasswordResetRequest } from "@pages/auth/VerifyPasswordResetRequest";
import { ResetPassword } from "@pages/auth/ResetPassword";
import { RequestPasswordReset } from "@pages/auth/RequestPasswordReset";
import { Settings } from "@pages/settings/Settings";
import { Onboarding } from "@pages/auth/Onboarding";
import { SkillQuizPage } from "@pages/content/skill/SkillQuizPage";
import { RegisterToBusinessGroup } from "@pages/auth/RegisterToBusinessGroup";
import { Invitation } from "@pages/auth/Invitation";
import HelpCenter from "@pages/help-center/HelpCenter";
import ContactUsPage from "@pages/contact-us/ContactUs";

import { AuthLayout } from "layouts/AuthLayout";
import { Login } from "../../pages";
import MainLayout from "../../layouts/MainLayout";
import FlowTab from "../../pages/content/flowTab/FlowTab";
import DetailsPage from "../../pages/content/details/DetailsPage";
import PlaylistsPage from "@pages/my-library/PlaylistsPage";
import PlaylistDetailsPage from "@pages/my-library/PlaylistDetailsPage";
import { CollectionComponent } from "@pages/content/collectionComponent/CollectionComponent";
import { Component } from "@pages/content/component/Component";
import { SubscribedAutoLogin } from "@pages/auth/SubscribedAutoLogin";

export interface Routes {
  "/login": {
    oAuth: string;
  };
  "/register": undefined;
  "/": {
    tabId: number;
  };
  "/details": {
    contentId: string;
  };
  [k: `/details/${string}/`]: {
    nextItemSource: number | "";
    playlistId: number | "";
  };
  [k: `/user/${string}/`]: undefined;
  "/login/email": undefined;
  "/login/phone": undefined;
  "/login/b2b": undefined;
  "/login/b2b/withToken": undefined;
  "/login/b2b/reset-password/verify": {
    create: string;
    email: string;
  };
  "/login/b2b/reset-password/new": {
    create: string;
    email: string;
    code: string;
  };
  "/login/reset-password/request": undefined;
  "/login/reset-password/verify": {
    create: string;
    email: string;
  };
  "/login/reset-password/new": {
    create: string;
    email: string;
    code: string;
  };
  "/login/subscribed": {
    token: string;
    refreshToken: string;
    forceAuthenticate: string;
  };
  "/settings": {
    tab: string;
  };
  "/onboarding": undefined;
  [k: `/skill/${string}/quiz`]: undefined;
  "/b2b/registration": {
    voucherCode: string;
  };
  "/invitation": {
    token: string;
    email: string;
    name: string;
  };
  "/help-center": undefined;
  "/contact-us": undefined;
  "/subscribe": undefined;
  "/playlists": undefined;
  "/playlist": {
    id: number | undefined;
  };
  "/cc": {
    type: string | undefined;
    id: string | undefined;
  };
  "/c": {
    type: string | undefined;
    id: string | undefined;
  };
}

export type RouteSearch<Path extends keyof Routes> =
  Routes[Path] extends undefined
    ? {
        [K in string | number]: never;
      }
    : Routes[Path];

export const routes: {
  pathname: keyof Routes;
  authenticated: boolean | null;
  Component: React.FunctionComponent;
  Layout?: React.FunctionComponent<{ children: React.ReactNode }>;
}[] = [
  {
    pathname: "/login",
    authenticated: false,
    Component: Login,
    Layout: AuthLayout,
  },
  {
    pathname: "/login/email",
    authenticated: false,
    Component: AuthByEmail,
    Layout: AuthLayout,
  },
  {
    pathname: "/login/phone",
    authenticated: false,
    Component: LoginByPhoneNumber,
    Layout: AuthLayout,
  },
  {
    pathname: "/login/b2b",
    authenticated: false,
    Component: B2BLogin,
    Layout: AuthLayout,
  },
  {
    pathname: "/login/b2b/withToken",
    authenticated: false,
    Component: B2BAutoLoginByToken,
    Layout: AuthLayout,
  },
  {
    pathname: "/login/b2b/reset-password/verify",
    authenticated: false,
    Component: VerifyPasswordResetRequest,
    Layout: AuthLayout,
  },
  {
    pathname: "/login/b2b/reset-password/new",
    authenticated: false,
    Component: ResetPassword,
    Layout: AuthLayout,
  },
  {
    pathname: "/b2b/registration",
    authenticated: false,
    Component: RegisterToBusinessGroup,
    Layout: AuthLayout,
  },
  {
    pathname: "/invitation",
    authenticated: false,
    Component: Invitation,
    Layout: AuthLayout,
  },
  {
    pathname: "/login/reset-password/request",
    authenticated: false,
    Component: RequestPasswordReset,
    Layout: AuthLayout,
  },
  {
    pathname: "/login/reset-password/verify",
    authenticated: false,
    Component: VerifyPasswordResetRequest,
    Layout: AuthLayout,
  },
  {
    pathname: "/login/reset-password/new",
    authenticated: false,
    Component: ResetPassword,
    Layout: AuthLayout,
  },
  {
    pathname: "/login/subscribed",
    authenticated: null,
    Component: SubscribedAutoLogin,
    Layout: AuthLayout,
  },
  {
    pathname: "/onboarding",
    authenticated: true,
    Component: Onboarding,
    Layout: AuthLayout,
  },
  {
    pathname: "/",
    authenticated: true,
    Component: FlowTab,
    Layout: MainLayout,
  },
  {
    pathname: "/details/:contentId/",
    authenticated: true,
    Component: DetailsPage,
    Layout: MainLayout,
  },
  {
    pathname: "/settings",
    authenticated: true,
    Component: Settings,
    Layout: MainLayout,
  },
  {
    pathname: "/skill/:skillId/quiz",
    authenticated: true,
    Component: SkillQuizPage,
    Layout: MainLayout,
  },
  {
    pathname: "/help-center",
    authenticated: true,
    Component: HelpCenter,
    Layout: MainLayout,
  },
  {
    pathname: "/contact-us",
    authenticated: true,
    Component: ContactUsPage,
    Layout: MainLayout,
  },
  {
    pathname: "/subscribe",
    authenticated: true,
    Component: () => {
      window.location.href =
        "https://pay.wajeez.com/?content=registerMethod&subscriptionType=P1Y";
      return null;
    },
  },
  {
    pathname: "/playlists",
    authenticated: true,
    Component: PlaylistsPage,
    Layout: MainLayout,
  },
  {
    pathname: "/playlist",
    authenticated: true,
    Component: PlaylistDetailsPage,
    Layout: MainLayout,
  },
  {
    pathname: "/cc",
    authenticated: true,
    Component: CollectionComponent,
    Layout: MainLayout,
  },
  {
    pathname: "/c",
    authenticated: true,
    Component: Component,
    Layout: MainLayout,
  },
];
